import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';

const ConfirmationDialogContext = createContext({
  getConfirmation: () => {},
});

const DEFAULT_DIALOG_STATE = {
  open: false,
  title: '',
  body: null,
  acceptText: 'OK',
  declineText: 'Cancel',
  onAccept: undefined,
  onDecline: undefined,
  acceptButtonProps: undefined,
  declineButtonProps: undefined,
  disableButtonAfterAction: false,
  enableBusyAdornment: undefined,
};

export function ConfirmationDialogProvider({ children }) {
  const [dialog, setDialog] = useState(DEFAULT_DIALOG_STATE);
  const [busyAction, setBusyAction] = useState(null);

  const closeDialog = useCallback(() => {
    setDialog(DEFAULT_DIALOG_STATE);
    setBusyAction(null);
  }, []);

  const getConfirmation = useCallback((config) => {
    setDialog({
      ...DEFAULT_DIALOG_STATE,
      ...config,
      open: true,
    });
  }, []);

  const runAction = useCallback(
    async (type) => {
      const action = type === 'accept' ? dialog.onAccept : dialog.onDecline;
      if (!action) {
        closeDialog();
        return;
      }

      try {
        if (dialog.disableButtonAfterAction) {
          setBusyAction(type);
        }
        await action();
      } finally {
        closeDialog();
      }
    },
    [closeDialog, dialog]
  );

  const value = useMemo(() => ({ getConfirmation }), [getConfirmation]);

  const isAcceptBusy =
    busyAction === 'accept' &&
    dialog.enableBusyAdornment === 'accept' &&
    dialog.disableButtonAfterAction;
  const isDeclineBusy =
    busyAction === 'decline' &&
    dialog.enableBusyAdornment === 'decline' &&
    dialog.disableButtonAfterAction;

  const handleDialogClose = useCallback(
    (_, reason) => {
      const isBusy = dialog.disableButtonAfterAction && busyAction !== null;
      if (isBusy && (reason === 'backdropClick' || reason === 'escapeKeyDown')) return;
      closeDialog();
    },
    [busyAction, closeDialog, dialog.disableButtonAfterAction]
  );

  return (
    <ConfirmationDialogContext.Provider value={value}>
      {children}
      <Dialog open={dialog.open} onClose={handleDialogClose} fullWidth maxWidth="sm">
        {dialog.title ? <DialogTitle>{dialog.title}</DialogTitle> : null}
        {dialog.body ? <DialogContent>{dialog.body}</DialogContent> : null}
        <DialogActions>
          <Button
            {...dialog.declineButtonProps}
            onClick={() => runAction('decline')}
            disabled={dialog.disableButtonAfterAction && busyAction !== null}
          >
            {isDeclineBusy ? <CircularProgress size={16} color="inherit" /> : dialog.declineText}
          </Button>
          <Button
            {...dialog.acceptButtonProps}
            onClick={() => runAction('accept')}
            disabled={dialog.disableButtonAfterAction && busyAction !== null}
          >
            {isAcceptBusy ? <CircularProgress size={16} color="inherit" /> : dialog.acceptText}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmationDialogContext.Provider>
  );
}

export function useConfirmationDialog() {
  return useContext(ConfirmationDialogContext);
}
